// MonetizeForm.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";

export default function MonetizeForm({
  mode,
  chatbotId,
  initialData,
  onClose,
  onSaveSuccess,
  permission,
}) {
  const formattedDate = new Date().toISOString().slice(0, 19).replace("T", " ");
  const defaultFormData = {
    title: "",
    days: 7,
    page_content: "",
    page_url: "",
    is_default: false,
    upload_date: formattedDate,
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData({
        id: initialData.id,
        title: initialData.title || "",
        days: initialData.days || 7,
        page_content: initialData.page_content || "",
        page_url: initialData.page_url || "",
        is_default: initialData.is_default || false,
      });
      setError(null);
    } else {
      setFormData(defaultFormData);
      setError(null);
    }
  }, [mode, initialData]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    const dataToSend = {
      ...formData,
      days: parseInt(formData.days, 10) || 7,
    };

    const isEditMode = mode === "edit";
    const url = isEditMode ? `/update-monetize/${formData.id}` : `/add-monetize`;
    const method = "post";

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance({ method, url, params, data: dataToSend });
      console.log("API response:", response.data);

      setLoading(false);
      onSaveSuccess();
      onClose();
    } catch (err) {
      console.error("Error submitting form:", err.response || err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "An unexpected error occurred.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  const formTitle = mode === "edit" ? "Edit Advertisement" : "Add New Advertisement";

  return (
      <Box component="form" onSubmit={handleSubmit} noValidate>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth required disabled={loading}>
              <InputLabel>Display Duration (Days)</InputLabel>
              <Select
                name="days"
                label="Display Duration (Days)"
                value={formData.days}
                onChange={handleChange}
              >
                <MenuItem value={7}>7 Days</MenuItem>
                <MenuItem value={15}>15 Days</MenuItem>
                <MenuItem value={30}>30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={4}
              label="Advertisement Details"
              name="page_content"
              value={formData.page_content}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              type="url"
              label="Target URL (Starts with http:// or https://)"
              name="page_url"
              placeholder="https://example.com"
              value={formData.page_url}
              onChange={handleChange}
              inputProps={{
                pattern: "https?://.*",
                title: "Please enter a valid URL starting with http:// or https://",
              }}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_default}
                  onChange={handleChange}
                  name="is_default"
                  color="primary"
                  disabled={loading}
                />
              }
              label="Set as Featured Advertisement?"
            />
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            disabled={loading}
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>

          {(mode === "edit" && permission.canEdit) ||
          (mode !== "edit" && permission.canAdd) ? (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
              startIcon={loading && <CircularProgress size={18} />}
            >
              {loading
                ? "Saving..."
                : mode === "edit"
                ? "Save Changes"
                : "Add Advertisement"}
            </Button>
          ) : null}
        </Box>
      </Box>
  );
}
